import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ApplyFormData } from '../ApplyFormComponent1';
import { useLocation } from 'react-router-dom';
import imageCompression from 'browser-image-compression';

export interface IDUploadFormRef {
  setFrontError: (err: string) => void;
  setBackError: (err: string) => void;
}

export interface IDUploadFormProps {
  onFrontFileChange?: (file: File | null) => void;
  onBackFileChange?: (file: File | null) => void;
}

function IDUploadFormInner(
  { onFrontFileChange, onBackFileChange }: IDUploadFormProps,
  ref: Ref<IDUploadFormRef>
) {
  const { setValue, watch } = useFormContext<ApplyFormData>();
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const maxFileSize = 15 * 1024 * 1024; // 15MB を超えたらエラー
  const compressThreshold = 1 * 1024 * 1024; // 1MB を超えたら圧縮

  const [citizenshipType, setCitizenshipType] = useState<
    'japan' | 'foreign' | ''
  >('');
  const idType = watch('idUploadData.idType');
  const frontFile = watch('idUploadData.frontFile');
  const backFile = watch('idUploadData.backFile');

  const [frontError, setFrontErr] = useState('');
  const [backError, setBackErr] = useState('');

  useImperativeHandle(ref, () => ({
    setFrontError: (err: string) => setFrontErr(err),
    setBackError: (err: string) => setBackErr(err),
  }));

  const handleCitizenshipChange = (value: 'japan' | 'foreign') => {
    setCitizenshipType(value);
    setValue('idUploadData.idType', '');
    setValue('idUploadData.frontFile', null);
    setValue('idUploadData.backFile', null);
    setFrontErr('');
    setBackErr('');
  };

  const japanIdOptions = useEnglish
    ? [
        "Driver's License (front/back)",
        'Health Insurance Card (front/back)',
        'My Number Card (front/back)',
        'Residence Card (front/back)',
        'Special Permanent Resident Certificate (front/back)',
        'Driving History Certificate (front/back)',
        'Seal Registration Certificate',
      ]
    : [
        '運転免許証(表・裏)',
        '健康保険証(表・裏)',
        'マイナンバーカード(表・裏)',
        '在留カード(表・裏)',
        '特別永住者証明書(表・裏)',
        '運転経歴証明書(表・裏)',
        '印鑑証明書',
      ];
  const foreignIdOptions = useEnglish ? ['Passport'] : ['パスポート'];

  const currentOptions =
    citizenshipType === 'japan'
      ? japanIdOptions
      : citizenshipType === 'foreign'
        ? foreignIdOptions
        : [];

  // My Number Card 用アラートの判定
  const showMyNumberAlert =
    idType === 'マイナンバーカード(表・裏)' ||
    idType === 'My Number Card (front/back)';

  // 表裏両面ドキュメント判定
  const isTwoSidedDoc =
    idType?.includes('(表・裏)') || idType?.includes('(front/back)');

  const getBaseLabel = (docType: string) =>
    docType
      .replace(/\(表・裏\)/g, '')
      .replace(/\(front\/back\)/g, '')
      .trim();
  const baseLabel = getBaseLabel(idType || '');

  const frontLabel = isTwoSidedDoc
    ? useEnglish
      ? `${baseLabel} (front)`
      : `${baseLabel}（表面）`
    : baseLabel;
  const backLabel = isTwoSidedDoc
    ? useEnglish
      ? `${baseLabel} (back)`
      : `${baseLabel}（裏面）`
    : '';

  const handleIdTypeChange = (val: string) => {
    setValue('idUploadData.idType', val);
    setValue('idUploadData.frontFile', null);
    setValue('idUploadData.backFile', null);
    setFrontErr('');
    setBackErr('');
  };

  const handleFrontFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File | null
  ) => {
    if (!file) {
      setValue('idUploadData.frontFile', null);
      onFrontFileChange?.(null);
      setFrontErr(
        useEnglish ? 'No file selected' : 'ファイルが選択されていません'
      );
      e.target.value = '';
      return;
    }
    if (file.size > maxFileSize) {
      setValue('idUploadData.frontFile', null);
      onFrontFileChange?.(null);
      setFrontErr(
        useEnglish
          ? 'Please select an image under 15MB.'
          : '容量が大きすぎます。15MB以下の画像を選択してください'
      );
      e.target.value = '';
      return;
    }
    try {
      let processed = file;
      if (file.size > compressThreshold) {
        processed = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });
      }
      setValue('idUploadData.frontFile', processed);
      onFrontFileChange?.(processed);
      setFrontErr('');
    } catch (err) {
      console.error('frontFile 圧縮エラー:', err);
      setValue('idUploadData.frontFile', file);
      onFrontFileChange?.(file);
      setFrontErr(
        useEnglish
          ? 'Failed to compress image, please select another file.'
          : '画像の圧縮に失敗しました。別のファイルを選択してください。'
      );
      e.target.value = '';
    }
  };
  // ── Back ファイル選択時の圧縮処理 ──
  const handleBackFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File | null
  ) => {
    if (!file) {
      setValue('idUploadData.backFile', null);
      onBackFileChange?.(null);
      setBackErr(
        useEnglish ? 'No file selected' : 'ファイルが選択されていません'
      );
      e.target.value = '';
      return;
    }
    if (file.size > maxFileSize) {
      setValue('idUploadData.backFile', null);
      onBackFileChange?.(null);
      setBackErr(
        useEnglish
          ? 'Please select an image under 15MB.'
          : '容量が大きすぎます。15MB以下の画像を選択してください'
      );
      e.target.value = '';
      return;
    }
    try {
      let processed = file;
      if (file.size > compressThreshold) {
        processed = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });
      }
      setValue('idUploadData.backFile', processed);
      onBackFileChange?.(processed);
      setBackErr('');
    } catch (err) {
      console.error('backFile 圧縮エラー:', err);
      setValue('idUploadData.backFile', file);
      onBackFileChange?.(file);
      setBackErr(
        useEnglish
          ? 'Failed to compress image, please select another file.'
          : '画像の圧縮に失敗しました。別のファイルを選択してください。'
      );
      e.target.value = '';
    }
  };
  return (
    <div className="bg-whiteYellow md:flex md:justify-center">
      <div className="md:max-w-[610px] w-full p-4 rounded-md bg-whiteYellow">
        <p className="text-sm mb-4 bg-white p-3">
          {useEnglish
            ? 'Identity verification documents may require both front and back uploads (e.g., a driver’s license), while some only require the front.'
            : '本人確認の書類は表・裏のアップロードが必要なもの（例：運転免許証）と、表のみものがあります。'}
          <br />
          {useEnglish
            ? 'Select the document type from the dropdown and attach the photo via the "Browse" button.'
            : '以下の「種類選択」から本人確認書類を選択し、「参照」ボタンより書類を撮影した画像を添付してください。'}
          <br />
          {useEnglish
            ? 'Only JPEG and PNG files under 15MB are accepted.'
            : '※送信できるのは15Mbyte以下のJPEG, PNG形式のファイルのみです。'}
          <br />
          {useEnglish
            ? 'For My Number Card, upload only the front.'
            : '※個人番号カードは表面のみアップロードしてください。'}
        </p>

        <div className="p-4 bg-whiteYellow">
          <h3 className="text-md font-bold mb-4">
            {useEnglish ? 'Identity Documents' : '本人確認書類'}
          </h3>

          {/* 国籍ラジオ */}
          <div className="mb-4 flex items-center gap-6">
            {/* 日本語／英語いずれも同じ */}
            <label className="flex items-center text-sm cursor-pointer">
              <input
                type="radio"
                name="citizenshipType"
                value="japan"
                checked={citizenshipType === 'japan'}
                onChange={() => handleCitizenshipChange('japan')}
                className="mr-1"
              />
              {useEnglish
                ? 'Japanese citizens or foreigners residing in Japan'
                : '日本国籍及び日本在住の外国籍の方'}
            </label>
            <label className="flex items-center text-sm cursor-pointer">
              <input
                type="radio"
                name="citizenshipType"
                value="foreign"
                checked={citizenshipType === 'foreign'}
                onChange={() => handleCitizenshipChange('foreign')}
                className="mr-1"
              />
              {useEnglish
                ? 'Foreigners applying from overseas'
                : '海外からのお申込みの外国籍の方'}
            </label>
          </div>

          {/* 種類選択 */}
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2">
              {useEnglish ? 'Select ID Type' : '種類選択'}
            </label>
            <select
              className="w-full border border-gray-300 p-2"
              value={idType}
              disabled={currentOptions.length === 0}
              onChange={(e) => handleIdTypeChange(e.target.value)}
            >
              <option value="">
                {useEnglish ? 'Please select' : '選択してください'}
              </option>
              {currentOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {idType === '' && currentOptions.length > 0 && (
              <p className="text-xs text-red-600 mt-1">
                {useEnglish ? 'Please select a type' : '種類を選択してください'}
              </p>
            )}
          </div>

          {showMyNumberAlert && (
            <p className="text-xs text-red-600 mb-4">
              {useEnglish
                ? 'Please mask the 12-digit personal number and QR code on your My Number Card.'
                : '※マイナンバーカード記載の個人番号（１２桁）及びQRコードは黒塗りの上、アップロードしてください。'}
            </p>
          )}

          {/* Front ファイル入力欄 */}
          {idType && (
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">
                {frontLabel}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="file"
                accept="image/jpeg, image/png"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  handleFrontFileChange(e, f);
                }}
                className="border border-gray-300 p-2 text-xs w-full md:w-auto"
              />
              {frontError && (
                <p className="text-xs text-red-600 mt-1">{frontError}</p>
              )}
              {frontFile && (
                <p className="text-xs text-gray-600 mt-1">
                  {useEnglish ? 'Selected: ' : '選択中: '}
                  {frontFile.name} / {frontFile.size} bytes
                </p>
              )}
            </div>
          )}

          {/* Back ファイル入力欄（2面必要な場合のみ） */}
          {isTwoSidedDoc && (
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">
                {backLabel}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="file"
                accept="image/jpeg, image/png"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  handleBackFileChange(e, f);
                }}
                className="border border-gray-300 p-2 text-xs w-full md:w-auto"
              />
              {backError && (
                <p className="text-xs text-red-600 mt-1">{backError}</p>
              )}
              {backFile && (
                <p className="text-xs text-gray-600 mt-1">
                  {useEnglish ? 'Selected: ' : '選択中: '}
                  {backFile.name} / {backFile.size} bytes
                </p>
              )}
              <p className="text-xs text-gray-600 mt-1">
                {useEnglish
                  ? 'Please mask the personal number on the document.'
                  : '※個人番号はマスキングの上、アップロードしてください。'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

IDUploadFormInner.displayName = 'IDUploadForm';
export const IDUploadForm = forwardRef<IDUploadFormRef, IDUploadFormProps>(
  IDUploadFormInner
);
